/**
 * CapabilityLedger — the contract-compliant replacement for the
 * three-equal-card capability grids (taste ban: three-equal-card feature
 * rows). A quiet two-column ledger: icon, capability, one-line description,
 * hairline dividers — no card boxes, no backdrop blur, no per-card chips.
 * Icons stay neutral (white/40): the page's gold budget belongs to the CTA.
 * Rows stagger in on first view (reduced motion: all visible immediately).
 */
import type { LucideIcon } from 'lucide-react';
import { motion, useReducedMotion, type HTMLMotionProps } from 'framer-motion';
import { FLOW_EASE } from './motion';

const ledgerMotion = {
  initial: 'hidden',
  whileInView: 'shown',
  viewport: { once: true, amount: 0.15 },
  variants: {
    hidden: {},
    shown: { transition: { staggerChildren: 0.07 } },
  },
} satisfies HTMLMotionProps<'div'>;

const rowMotion = {
  variants: {
    hidden: { opacity: 0, y: 12 },
    shown: { opacity: 1, y: 0, transition: { duration: 0.45, ease: FLOW_EASE } },
  },
} satisfies HTMLMotionProps<'div'>;

export interface CapabilityItem {
  icon: LucideIcon;
  title: string;
  desc: string;
}

export function CapabilityLedger({
  items,
  columns = 2,
  className = '',
}: {
  items: CapabilityItem[];
  columns?: 1 | 2;
  className?: string;
}) {
  const reduced = useReducedMotion();

  const Div = reduced ? 'div' : motion.div;

  return (
    <Div
      className={`grid grid-cols-1 ${
        columns === 2 ? 'md:grid-cols-2 md:gap-x-14' : ''
      } gap-y-1 ${className}`}
      {...(reduced ? {} : ledgerMotion)}
    >
      {items.map(({ icon: Icon, title, desc }) => (
        <Div
          key={title}
          className="flex items-start gap-4 py-4 border-t border-white/[0.06]"
          {...(reduced ? {} : rowMotion)}
        >
          <Icon className="mt-0.5 size-[18px] text-white/40 shrink-0" aria-hidden />
          <div>
            <h3 className="text-sm font-semibold text-white">{title}</h3>
            <p className="text-sm text-white/50 leading-relaxed mt-0.5">{desc}</p>
          </div>
        </Div>
      ))}
    </Div>
  );
}
